import { useState, useEffect, useCallback } from 'react';
import { requestJustification, myJustifications } from '../lib/justify';
import { getPolicy } from '../lib/policy';
import { localDateISO } from '../lib/supabase';
import { CertificateField, CertificateLink } from './Certificate';

const fmt = (s) => (s ? new Date(s + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const ST = {
  pending: ['Pendiente', 'bg-amber-50 text-amber-700'],
  approved: ['Justificada', 'bg-emerald-50 text-emerald-700'],
  rejected: ['No aprobada', 'bg-red-50 text-red-600'],
};

// Para el empleado: pedir que una falta quede justificada, con certificado
export default function JustificarFalta({ profile, onToast }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: '', reason: '', path: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const requiresCert = !!getPolicy().payroll.justification_requires_certificate;
  const deduct = Number(profile.justified_deduct_pct ?? getPolicy().payroll.justified_deduct_pct ?? 0);
  const load = useCallback(async () => setList(await myJustifications(profile.id).catch(() => [])), [profile.id]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setErr('');
    if (!f.date) return setErr('Elegí el día de la falta');
    if (f.reason.trim().length < 3) return setErr('Contanos el motivo');
    if (requiresCert && !f.path) return setErr('Tu organización pide adjuntar un certificado');
    setBusy(true);
    try {
      await requestJustification(f.date, f.reason, f.path);
      setOpen(false); setF({ date: '', reason: '', path: null });
      await load(); onToast?.('Pedido enviado: lo revisa tu administrador');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      {!open ? (
        <button onClick={() => setOpen(true)} className="w-full py-3.5 rounded-2xl text-sm font-bold text-sky-800 bg-sky-50 border border-sky-100 active:scale-95 transition-all">
          Justificar una falta con certificado
        </button>
      ) : (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 pt-5 pb-3 border-b border-gray-50 flex items-center justify-between">
            <h3 className="font-bold text-gray-900 text-sm">Justificar una falta</h3>
            <button onClick={() => { setOpen(false); setErr(''); }} className="text-gray-400 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100">✕</button>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-xs text-gray-500 leading-relaxed">
              Si faltaste y tenés un motivo (salud, trámite, duelo…), pedí acá que se justifique. Tu administrador lo revisa y te avisa.
              {deduct <= 0 ? ' Una falta justificada no se descuenta de tu sueldo.' : ` Una falta justificada se descuenta al ${deduct}% según las reglas de tu organización.`}
            </p>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Día de la falta</label>
              <input type="date" value={f.date} max={localDateISO()} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Motivo</label>
              <input value={f.reason} maxLength={500} onChange={(e) => setF((p) => ({ ...p, reason: e.target.value }))} placeholder="Ej.: consulta médica, trámite, duelo…"
                className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <CertificateField orgId={profile.organization_id} employeeId={profile.id} value={f.path} onChange={(p) => setF((x) => ({ ...x, path: p }))} required={requiresCert} />
            <p className="text-[11px] text-gray-400">Los certificados pueden tener datos de salud: solo los ve tu administrador. Subí únicamente lo necesario.</p>
            {err && <p className="text-red-500 text-xs bg-red-50 px-3 py-2.5 rounded-xl">{err}</p>}
            <button onClick={send} disabled={busy} className="w-full py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--brand-grad)' }}>
              {busy ? 'Enviando…' : 'Enviar pedido'}
            </button>
          </div>
        </div>
      )}
      {list.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50"><h3 className="font-bold text-gray-800 text-sm">Mis pedidos de justificación</h3></div>
          <div className="divide-y divide-gray-50">
            {list.map((r) => {
              const [label, cls] = ST[r.status] || ST.pending;
              return (
                <div key={r.id} className="px-5 py-3.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${cls}`}>{label}</span>
                    <span className="text-sm font-semibold text-gray-800">{fmt(r.date)}</span>
                    <CertificateLink path={r.certificate_path} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1 italic">"{r.reason}"</p>
                  {r.admin_note && <p className="text-xs text-sky-700 bg-sky-50 rounded-xl px-3 py-2 mt-2">Administrador: "{r.admin_note}"</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
